import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { authAPI, setupAPI, healthAPI, SERVER_UNAVAILABLE_EVENT } from './services/api';
import Navbar from './components/Navbar';
import OfflineBanner from './components/OfflineBanner';
import Dashboard from './pages/Dashboard';
import Household from './pages/Household';
import Stats from './pages/Stats';
import BillManager from './pages/BillManagerV2';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import Login from './pages/Login';
import OAuthCallback from './pages/OAuthCallback';
import AcceptInvite from './pages/AcceptInvite';
import SetupWizard from './pages/SetupWizard';
import StartupScreen from './components/StartupScreen';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { Menu } from 'lucide-react';
import './styles/App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Initialize collapsed state based on window width
  const [navCollapsed, setNavCollapsed] = useState(() => {
    const width = window.innerWidth;
    return width >= 768 && width < 1200; // Auto-collapse in middle zone
  });
  const [userManuallyToggledNav, setUserManuallyToggledNav] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [serverReady, setServerReady] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isServerUnavailable, setIsServerUnavailable] = useState(false);

  // Check server readiness first, then setup/auth
  useEffect(() => {
    checkServerReady();
    // Register service worker for PWA
    serviceWorkerRegistration.register();
    
    // Listen for online/offline events
    const handleOnline = () => {
      setIsOffline(false);
      // If server wasn't ready yet, show server unavailable and let auto-retry handle it
      if (!serverReady) {
        setIsServerUnavailable(true);
      }
    };
    const handleOffline = () => setIsOffline(true);
    
    // Listen for global server unavailable events from API interceptor
    const handleServerUnavailableEvent = () => {
      console.log('[App] Server unavailable event received');
      setServerReady(false);
      setIsServerUnavailable(true);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener(SERVER_UNAVAILABLE_EVENT, handleServerUnavailableEvent);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener(SERVER_UNAVAILABLE_EVENT, handleServerUnavailableEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkServerReady = async () => {
    const maxAttempts = 30; // 30 seconds max wait
    let attempts = 0;

    // Check if we're offline - show offline screen
    if (!navigator.onLine) {
      console.log('App is offline');
      setIsOffline(true);
      return;
    }

    const poll = async () => {
      try {
        const response = await healthAPI.checkReady();
        if (response.data.ready) {
          setServerReady(true);
          // Server is ready, now check setup and auth
          checkSetupAndAuth();
          return;
        }
      } catch (error) {
        // Check if we went offline during polling
        if (!navigator.onLine) {
          console.log('App went offline');
          setIsOffline(true);
          return;
        }
        // Server not ready yet, continue polling
      }

      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 1000); // Poll every second
      } else {
        // Max attempts reached - check if we're actually offline
        if (!navigator.onLine) {
          console.log('Server check timed out - device is offline');
          setIsOffline(true);
        } else {
          // Online but server not responding
          console.warn('Server readiness check timed out - server may be unavailable');
          setIsServerUnavailable(true);
        }
      }
    };

    poll();
  };

  const checkSetupAndAuth = async () => {
    try {
      // First check if setup is needed
      const setupResponse = await setupAPI.getStatus();
      
      if (setupResponse.data.needsSetup) {
        setNeedsSetup(true);
        setLoading(false);
        return;
      }
      
      // Setup complete, check for existing auth
      await checkAuth();
    } catch (error) {
      console.error('Setup/Auth check failed:', error);
      // If setup check fails, try auth anyway
      await checkAuth();
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Verify token is still valid by getting current user
      const response = await authAPI.getCurrentUser();
      const { user: userData, household: householdData } = response.data;
      
      // Update localStorage with fresh data (in case household membership changed)
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('household', JSON.stringify(householdData));
      
      setUser(userData);
      setHousehold(householdData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth check failed:', error);
      // Token invalid - clear everything
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('household');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, householdData) => {
    setUser(userData);
    setHousehold(householdData);
    setIsAuthenticated(true);
  };

  const handleSetupComplete = (userData, householdData) => {
    setUser(userData);
    setHousehold(householdData);
    setIsAuthenticated(true);
    setNeedsSetup(false);
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API response
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('household');
      setUser(null);
      setHousehold(null);
      setIsAuthenticated(false);
    }
  };

  const toggleNav = () => {
    setNavCollapsed(!navCollapsed);
    setUserManuallyToggledNav(true);
  };

  const toggleMobileNav = () => {
    setMobileNavOpen(!mobileNavOpen);
  };

  // Retry handler for server unavailable state - returns a promise
  // Does NOT change screen state - lets StartupScreen handle the UI
  const handleRetry = () => {
    return new Promise((resolve) => {
      // Single check - don't change any state that would switch screens
      healthAPI.checkReady()
        .then((response) => {
          if (response.data.ready) {
            setServerReady(true);
            setIsServerUnavailable(false);
            checkSetupAndAuth();
          }
          resolve();
        })
        .catch(() => {
          // Still unavailable - no state change needed
          resolve();
        });
    });
  };

  // Handler for when login detects server is unavailable
  const handleServerUnavailable = () => {
    setServerReady(false);
    setIsServerUnavailable(true);
  };

  // Show offline screen if no connection
  if (isOffline) {
    return (
      <ThemeProvider>
        <StartupScreen isOffline={true} onRetry={handleRetry} />
      </ThemeProvider>
    );
  }

  // Show server unavailable screen if timeout
  if (isServerUnavailable) {
    return (
      <ThemeProvider>
        <StartupScreen isServerUnavailable={true} onRetry={handleRetry} />
      </ThemeProvider>
    );
  }

  // Show startup screen while waiting for server
  if (!serverReady) {
    return (
      <ThemeProvider>
        <StartupScreen message="Connecting to server..." />
      </ThemeProvider>
    );
  }

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <ThemeProvider>
        <StartupScreen message="Loading..." />
      </ThemeProvider>
    );
  }

  // Show setup wizard if setup is needed
  if (needsSetup) {
    return (
      <ThemeProvider>
        <SetupWizard onComplete={handleSetupComplete} />
      </ThemeProvider>
    );
  }

  // Handle OAuth callback route even when not authenticated
  // This needs to be handled before the login check
  if (!isAuthenticated && window.location.pathname === '/oauth/callback') {
    return (
      <ThemeProvider>
        <Router>
          <Routes>
            <Route path="/oauth/callback" element={<OAuthCallback onLogin={handleLogin} />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  // Handle invitation acceptance when not authenticated
  if (!isAuthenticated && window.location.pathname === '/accept-invite') {
    return (
      <ThemeProvider>
        <Router>
          <Routes>
            <Route path="/accept-invite" element={<AcceptInvite onLogin={handleLogin} />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} onServerUnavailable={handleServerUnavailable} />;
  }

  return (
    <ThemeProvider>
      <ToastProvider>
        <Router>
          <AppContent 
            isAuthenticated={isAuthenticated}
            navCollapsed={navCollapsed}
            setNavCollapsed={setNavCollapsed}
            userManuallyToggledNav={userManuallyToggledNav}
            setUserManuallyToggledNav={setUserManuallyToggledNav}
            toggleNav={toggleNav}
            mobileNavOpen={mobileNavOpen}
            toggleMobileNav={toggleMobileNav}
            handleLogout={handleLogout}
            user={user}
            household={household}
          />
        </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

// Separate component to access useLocation
function AppContent({ isAuthenticated, navCollapsed, setNavCollapsed, userManuallyToggledNav, setUserManuallyToggledNav, toggleNav, mobileNavOpen, toggleMobileNav, handleLogout, user, household }) {
  const location = useLocation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const prevWidthRef = React.useRef(window.innerWidth);

  // Track window width for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const prevWidth = prevWidthRef.current;
      
      // Determine breakpoint zones
      const wasInAutoCollapse = prevWidth >= 768 && prevWidth < 1200;
      const isInAutoCollapse = newWidth >= 768 && newWidth < 1200;
      const wasFullWidth = prevWidth >= 1200;
      const isFullWidth = newWidth >= 1200;
      
      // Auto-collapse when entering the 768-1199 zone (unless user manually expanded)
      if (!wasInAutoCollapse && isInAutoCollapse && !userManuallyToggledNav) {
        setNavCollapsed(true);
      }
      
      // Auto-expand when entering full width zone (unless user manually collapsed)
      if (!wasFullWidth && isFullWidth && !userManuallyToggledNav) {
        setNavCollapsed(false);
      }
      
      // Reset manual toggle flag when going from auto-collapse to full width
      if (wasInAutoCollapse && isFullWidth) {
        setUserManuallyToggledNav(false);
        setNavCollapsed(false);
      }
      
      prevWidthRef.current = newWidth;
      setWindowWidth(newWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [userManuallyToggledNav, setNavCollapsed, setUserManuallyToggledNav]);

  // Close mobile nav on route change
  useEffect(() => {
    if (mobileNavOpen) {
      toggleMobileNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Three-stage responsive behavior:
  // Stage 1 (>= 1200px): Full navbar, respect manual toggle
  // Stage 2 (768px - 1199px): Auto-collapse to icons, can expand with toggle
  // Stage 3 (< 768px): Navbar hidden, mobile hamburger menu
  const isMobile = windowWidth < 768;

  // Check maintenance mode on route change
  useEffect(() => {
    const checkMaintenanceOnNav = async () => {
      if (!isAuthenticated || user?.role === 'admin') return;

      try {
        await authAPI.getCurrentUser();
      } catch (error) {
        // If we get a 503 maintenance error, user will be redirected by interceptor
        // No need to handle here
      }
    };

    checkMaintenanceOnNav();
  }, [location.pathname, isAuthenticated, user]);

  return (
    <div className={`app ${isMobile ? 'mobile-view' : ''}`}>
      {/* Mobile Header - only shows on mobile */}
      {isMobile && (
        <header className="mobile-header">
          <button className="mobile-menu-btn" onClick={toggleMobileNav}>
            <Menu size={24} />
          </button>
                    <h1 className="mobile-title">Remidy</h1>
        </header>
      )}
      
      {/* Mobile overlay */}
      {isMobile && mobileNavOpen && (
        <div className="mobile-nav-overlay" onClick={toggleMobileNav} />
      )}
      
      <Navbar 
        collapsed={isMobile ? false : navCollapsed} 
        onToggle={isMobile ? toggleMobileNav : toggleNav} 
        onLogout={handleLogout} 
        user={user}
        mobileOpen={mobileNavOpen}
      />
      
      <main className={`main-content ${navCollapsed && !isMobile ? 'nav-collapsed' : ''} ${isMobile ? 'mobile' : ''}`}>
        <Routes>
          <Route path="/" element={<Navigate to="/overview" replace />} />
          <Route path="/overview" element={<Dashboard user={user} />} />
          <Route path="/manage" element={<BillManager />} />
          <Route path="/household" element={<Household user={user} household={household} />} />
          <Route path="/calendar" element={<Navigate to="/manage" replace />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/settings" element={<Settings user={user} />} />
          {/* Legacy redirects */}
          <Route path="/bills" element={<Navigate to="/manage" replace />} />
          <Route path="/payment-activity" element={<Navigate to="/manage" replace />} />
          <Route path="/bill-manager" element={<Navigate to="/manage" replace />} />
          <Route path="*" element={<Navigate to="/overview" replace />} />
        </Routes>
      </main>
      
      {/* Offline Banner */}
      <OfflineBanner />
    </div>
  );
}

export default App;
